import React from 'react';
import { Button } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { ReasonModal, useReasonPrompt } from '../../pages/hospital/patient/ReasonPromptModal';
import { webNotAvailableMark } from '../../utils/shotNotAvailable';

/**
 * SKIN-726 — whether the submission being edited may have a shot marked "not
 * available" from the web. The patient page decides it (`mayMarkShotNotAvailable`:
 * the web close gate on a DRAFT visit) and provides it around the editor, so the
 * form layers in between do not carry it. Default false: any other place that renders
 * the editor offers nothing.
 */
export const ShotNotAvailableContext = React.createContext<boolean>(false);

interface ShotNotAvailableMarkProps {
    /** the shot's own "not available" label from the eCRF, e.g. "No healthy skin available" */
    label: string;
    /** receives the marker to store; the form's save sends it */
    onMark: (value: string) => void;
}

/**
 * The "Mark as …" action and its reason dialog. A blank reason is refused here, as
 * the API refuses it; confirming hands the marker to the form, which saves it with
 * the rest of the edit.
 */
const ShotNotAvailableMark: React.FC<ShotNotAvailableMarkProps> = ({ label, onMark }) => {
    const prompt = useReasonPrompt();

    // The marker is handed over once the dialog has closed: marking the shot hides this
    // action, and the prompt must not be updating state while it unmounts.
    const confirm = async () => {
        let mark: string | null = null;
        await prompt.submit({
            requiredKey: 'hospital.visit-status-reason-required',
            action: async (reason) => { mark = webNotAvailableMark(reason); },
            resolveError: () => '',
        });
        if (mark !== null) onMark(mark);
    };

    return (
        <>
            <Button size="small" variant="outlined" sx={{ mt: 1, textTransform: 'none' }} onClick={() => prompt.open(0)}>
                <Translate id="hospital.fg-edit-image-mark-not-available" data={{ label }} />
            </Button>
            <ReasonModal
                prompt={prompt}
                title={<Translate id="hospital.fg-edit-image-not-available-title" />}
                body={<Translate id="hospital.fg-edit-image-not-available-body" data={{ label }} />}
                reasonLabel={<Translate id="hospital.visit-status-reason" />}
                confirmLabel="hospital.fg-edit-image-not-available-confirm"
                onConfirm={confirm}
            />
        </>
    );
};

export default ShotNotAvailableMark;
